import {
    createRouter,
    createWebHistory,
    type RouterScrollBehavior,
    RouterView,
} from "vue-router";
import { h } from "vue";
import { i18n } from "@/i18n";
import HomePage from "@/views/HomePage.vue";
import { adminRoutes } from "@/router/admin";
import { installChunkReload } from "@/router/chunkReload";

const scrollBehavior: RouterScrollBehavior = async (
    to,
    from,
    savedPosition,
) => {
    if (savedPosition) return savedPosition;

    if (to.hash) {
        return { el: to.hash, top: 90, behavior: "smooth" };
    }

    return { top: 0, behavior: "smooth" };
};

const routes = [
    { path: "", name: "home", component: HomePage },
    {
        path: "projects",
        name: "projects",
        component: () => import("@/views/ProjectsPage.vue"),
    },
    {
        path: "projects/:slug",
        name: "project-details",
        component: () => import("@/views/ProjectDetailsPage.vue"),
        props: true,
    },
    {
        path: "team",
        name: "team",
        component: () => import("@/views/TeamPage.vue"),
    },
    {
        path: "careers",
        name: "careers",
        component: () => import("@/views/CareersPage.vue"),
    },
    {
        path: "careers/:id",
        name: "career-detail",
        component: () => import("@/views/CareerDetailPage.vue"),
        props: true,
    },
    {
        path: "contact",
        name: "contact",
        component: () => import("@/views/ContactPage.vue"),
    },
    {
        path: "outsourcing",
        name: "outsourcing",
        component: () => import("@/views/OutsourcingPage.vue"),
    },
    {
        path: "outsourcing/apply",
        name: "outsourcing-apply",
        component: () => import("@/views/OutsourcingApplyPage.vue"),
    },
    {
        path: "outsourcing/book",
        name: "outsourcing-book",
        component: () => import("@/views/OutsourcingBookPage.vue"),
    },
    {
        path: "insights",
        name: "insights",
        component: () => import("@/views/InsightsOverviewPage.vue"),
    },
    {
        path: "insights/:slug",
        name: "insight-detail",
        component: () => import("@/views/InsightsDetailPage.vue"),
        props: true,
    },
    {
        path: "success-stories",
        name: "success-stories",
        component: () => import("@/views/SuccessStoriesOverviewPage.vue"),
    },
    {
        path: "success-stories/:slug",
        name: "success-story-detail",
        component: () => import("@/views/SuccessStoriesDetailPage.vue"),
        props: true,
    },
    {
        path: "proposal/:uuid",
        name: "proposal",
        component: () => import("@/views/ProposalPage.vue"),
        props: true,
    },
    {
        path: "interview/:uuid",
        name: "interview",
        component: () => import("@/views/InterviewPage.vue"),
        props: true,
    },
    {
        path: "freelancer/:slug",
        name: "freelancer-cv",
        component: () => import("@/views/FreelancerCvPage.vue"),
        props: true,
    },
    { path: ":pathMatch(.*)*", redirect: "/" },
];

export const router = createRouter({
    history: createWebHistory(),
    scrollBehavior,
    routes: [
        ...adminRoutes,
        {
            path: "/:locale(en|nl)?",
            component: { render: () => h(RouterView) },
            children: routes,
        },
    ],
});

installChunkReload(router);

router.beforeEach((to, from, next) => {
    // Admin routes bypass locale handling
    if (to.path.startsWith("/admin")) {
        document.title = "Admin – BitPeak";
        updateRobotsTag("admin");

        const token = localStorage.getItem("admin_token");

        if (to.name === "admin-login") {
            return token ? next("/admin/proposals") : next();
        }

        if (to.meta.requiresAdminAuth && !token) {
            return next("/admin/login");
        }

        return next();
    }

    const locale = to.params.locale as string;
    const supportedLocales = ["en", "nl"];

    // If no locale is present, redirect to default (en)
    if (!locale) {
        return next(`/en${to.fullPath}`);
    }

    // Resolve the locale we'll use (fallback to 'en')
    const resolvedLocale =
        locale && supportedLocales.includes(locale) ? locale : "en";

    // Update HTML lang attribute
    const html = document.documentElement;
    html.setAttribute("lang", resolvedLocale);

    // Ensure i18n is set to the resolved locale before updating title/meta
    try {
        // @ts-ignore
        if (
            i18n &&
            i18n.global &&
            i18n.global.locale &&
            i18n.global.locale.value !== resolvedLocale
        ) {
            // @ts-ignore
            i18n.global.locale.value = resolvedLocale;
        }
    } catch (e) {
        // ignore if i18n isn't ready
        // console.warn('i18n not ready', e);
    }

    // Update title + description from translations
    const title = resolveSeo("title", to.name as string);
    const description = resolveSeo("description", to.name as string);
    document.title = title;
    updateDescription(description);

    // Update canonical and alternate links
    updateMetaTags(to.path, resolvedLocale);
    updateSocialTags(title, description, resolvedLocale);
    updateRobotsTag(to.name as string);

    next();
});

const SEO_FALLBACK = {
    title: "BitPeak | AI-native software studio The Hague",
    description:
        "AI-native software studio in The Hague. From smart web apps to custom AI: fixed price, live in 6 weeks on average.",
};

/**
 * Per-page title/description, falling back to the site-level one.
 * Route names are kebab-case, the translation keys snake_case.
 */
const resolveSeo = (field: "title" | "description", routeName: string) => {
    const pageKey = `meta.pages.${(routeName ?? "").replace(/-/g, "_")}.${field}`;

    try {
        // @ts-ignore — Composer exposes te()/t() on the global instance
        if (routeName && i18n.global.te(pageKey)) {
            // @ts-ignore
            const page = i18n.global.t(pageKey) as string;
            if (page) return page;
        }

        // @ts-ignore
        const site = i18n.global.t(`meta.${field}`) as string;
        if (site) return site;
    } catch (e) {
        // i18n not ready — fall through to the hardcoded default
    }

    return SEO_FALLBACK[field];
};

const updateDescription = (description: string) => {
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "description");
        document.head.appendChild(tag);
    }
    tag.setAttribute("content", description);
};

/**
 * Keep Open Graph / Twitter cards in sync with the resolved page meta. The blade
 * shell only ships static defaults, so without this every shared URL would show
 * the homepage copy in English.
 */
const updateSocialTags = (
    title: string,
    description: string,
    currentLocale: string,
) => {
    const properties: Record<string, string> = {
        "og:title": title,
        "og:description": description,
        "og:url": window.location.href.split("#")[0],
        "og:locale": currentLocale === "nl" ? "nl_NL" : "en_US",
    };

    Object.entries(properties).forEach(([property, content]) => {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (!tag) {
            tag = document.createElement("meta");
            tag.setAttribute("property", property);
            document.head.appendChild(tag);
        }
        tag.setAttribute("content", content);
    });

    const names: Record<string, string> = {
        "twitter:title": title,
        "twitter:description": description,
    };

    Object.entries(names).forEach(([name, content]) => {
        let tag = document.querySelector(`meta[name="${name}"]`);
        if (!tag) {
            tag = document.createElement("meta");
            tag.setAttribute("name", name);
            document.head.appendChild(tag);
        }
        tag.setAttribute("content", content);
    });
};

const updateRobotsTag = (routeName: string) => {
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.setAttribute("name", "robots");
        document.head.appendChild(robotsMeta);
    }

    if (
        routeName === "proposal" ||
        routeName === "interview" ||
        routeName === "freelancer-cv" ||
        routeName === "admin"
    ) {
        robotsMeta.setAttribute("content", "noindex, nofollow");
    } else {
        robotsMeta.setAttribute("content", "index, follow");
    }
};

const updateMetaTags = (path: string, currentLocale: string) => {
    // Remove existing tags
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) existingCanonical.remove();

    const existingAlternates = document.querySelectorAll(
        'link[rel="alternate"]',
    );
    existingAlternates.forEach((el) => el.remove());

    const baseUrl = window.location.origin;

    // Clean path: remove locale prefix if present to get the "route" part
    let routePath = path;
    if (routePath.startsWith("/en")) routePath = routePath.substring(3);
    else if (routePath.startsWith("/nl")) routePath = routePath.substring(3);
    if (!routePath.startsWith("/")) routePath = "/" + routePath;

    // Canonical (ensure canonical includes the resolved locale)
    const canonicalLink = document.createElement("link");
    canonicalLink.setAttribute("rel", "canonical");
    let canonicalHref = `${baseUrl}/${currentLocale}${routePath}`;
    canonicalHref = canonicalHref.replace(/([^:]\/)\/+/g, "$1");
    canonicalLink.setAttribute("href", canonicalHref);
    document.head.appendChild(canonicalLink);

    // Alternates
    const supportedLocales = ["en", "nl"];
    supportedLocales.forEach((loc) => {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", loc);

        let href = `${baseUrl}/${loc}${routePath}`;
        // Fix double slashes
        href = href.replace(/([^:]\/)\/+/g, "$1");

        link.setAttribute("href", href);
        document.head.appendChild(link);
    });

    // x-default
    const xDefault = document.createElement("link");
    xDefault.setAttribute("rel", "alternate");
    xDefault.setAttribute("hreflang", "x-default");
    // Default is English
    let defaultHref = `${baseUrl}/en${routePath}`;
    defaultHref = defaultHref.replace(/([^:]\/)\/+/g, "$1");
    xDefault.setAttribute("href", defaultHref);
    document.head.appendChild(xDefault);
};

/**
 * Per-article head override. `beforeEach` can only emit the generic Insights meta
 * because the post isn't fetched yet, and it builds alternates by swapping the
 * locale prefix — which breaks for Dutch articles that carry their own slug. The
 * detail page calls this once it knows the real title and both slugs.
 */
export const applyArticleSeo = (opts: {
    title: string;
    description: string;
    locale: string;
    alternates: Partial<Record<"en" | "nl", string>>;
}) => {
    const { title, description, locale, alternates } = opts;

    document.title = title;
    updateDescription(description);
    updateSocialTags(title, description, locale);

    document.querySelector('link[rel="canonical"]')?.remove();
    document
        .querySelectorAll('link[rel="alternate"]')
        .forEach((el) => el.remove());

    const origin = window.location.origin;
    const abs = (href: string) =>
        href.startsWith("http") ? href : `${origin}${href}`;

    const appendLink = (rel: string, href: string, hreflang?: string) => {
        const link = document.createElement("link");
        link.setAttribute("rel", rel);
        if (hreflang) link.setAttribute("hreflang", hreflang);
        link.setAttribute("href", abs(href));
        document.head.appendChild(link);
    };

    const self = alternates[locale as "en" | "nl"];
    if (self) appendLink("canonical", self);

    (["en", "nl"] as const).forEach((loc) => {
        const href = alternates[loc];
        if (href) appendLink("alternate", href, loc);
    });

    if (alternates.en) appendLink("alternate", alternates.en, "x-default");
};
